import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { GET_CURRENT_USER_QUERY_KEY } from '@/hooks/api/users/useGetCurrentUser';
import { AuthService } from '../types';
import { useCompareCart } from '@/services/compareCart/useCompareCart';

export interface OidcAuthService extends AuthService {
  login(): Promise<void>;
}

export const useOidcAuth = (): OidcAuthService => {
  const queryClient = useQueryClient();
  const oidcAuth = useAuth();
  const { clearCart } = useCompareCart();

  const isAuthenticated = oidcAuth.isAuthenticated && !oidcAuth.isLoading;

  const login = async () => {
    await oidcAuth.signinRedirect();
  };

  const logout = async () => {
    await oidcAuth.removeUser();
    queryClient.resetQueries({ queryKey: [GET_CURRENT_USER_QUERY_KEY] });
    clearCart();
    // Standard OIDC end-session flow — oidc-client-ts hits the issuer's
    // end_session_endpoint advertised by the discovery doc. Cognito's hosted UI
    // only honors its proprietary logout_uri parameter, so send it there too.
    const issuerHost = new URL(oidcAuth.settings.authority).hostname;
    const isCognitoIssuer = issuerHost.startsWith('cognito-idp.') && issuerHost.endsWith('.amazonaws.com');
    await oidcAuth.signoutRedirect(
      isCognitoIssuer ? { extraQueryParams: { logout_uri: import.meta.env.VITE_OIDC_SIGNOUT_URI } } : undefined
    );
  };

  const getCurrentAccessToken = (): string | undefined => {
    return oidcAuth.user?.access_token;
  };

  return {
    authType: 'oidc-federated',
    isAuthenticated,
    isLoading: oidcAuth.isLoading,
    login,
    logout,
    getCurrentAccessToken,
  };
};
